import React, { useState } from 'react';

import { AdvancedSearch, InputFormat, SectionHeading, InputWithEnterEvent, TableCustom } from '../../components/common';
import { useAccess, useForm, useLista, useManagedContext, useNav } from '../../components/hooks';
import { ALERT_TYPE, MASK_FORMAT, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { onRequestError, standardRequest } from '../../utils/requests';
import { APIS } from '../../config/apis';
import { VALIDATION_MESSAGE } from '../../consts/validationMessage';
import { BUSINESS_CODE } from '../../consts/businessCode';

function HomeView() {
    //#region Hooks

    const navigate = useNav()
    const { setIsLoading } = useManagedContext()

    const [formValues, formHandle, , formSet] = useForm({
        numeroDocumento: '',
        nombre: '',
    });

    const [labels, setLabels] = useState([
        { title: 'Documento', field: 'numeroDocumento', value: '', valueIgnore: ''},
        { title: 'Nombre', field: 'nombre', value: '', valueIgnore: '' },
    ]);

    const [personas, setPersonas] = useState([])

    const [tableVisible, setTableVisible] = useState(false)
    const [searchMessage, setSearchMessage] = useState(null)

    const [access, setAccess] = useState({
        fisica: { view: false, edit: false, },
        juridica: { view: false, edit: false, },
    })

    useAccess({
        key: 'TableCustom',
        onLoaded: (data, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error)
            else {
                const newAccess = access;
                data.forEach(item => {
                    if (item.codigo === 'persona-fisica_view') access.fisica.view = true;
                    if (item.codigo === 'persona-fisica_edit') access.fisica.edit = true;
                    if (item.codigo === 'persona-juridica_view') access.juridica.view = true;
                    if (item.codigo === 'persona-juridica_edit') access.juridica.edit = true;
                })
                setAccess(newAccess)
            }
        }
    });

    const [, getRowLista] = useLista({
        listas: ['TipoDocumento'    ],
        onLoaded: (listas, isSuccess, error) => {
          if (isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
        },
        memo: {
          key: 'TipoDocumento',
          timeout: 0
        }
    });

    //#endregion

    //#region Search

    const hasActiveFilters = Object.values(formValues).some(value => String(value ?? '').trim() !== '')

    const SearchPersonas = async () => {
        setIsLoading(true)
        setSearchMessage(null)

        const params = `/filter?`+
            `numeroDocumento=${encodeURIComponent(formValues.numeroDocumento)}&`+
            `nombre=${encodeURIComponent(formValues.nombre)}&`+
            `etiqueta=`;

        const personasFisicasRequest = async () => {
            if (access.fisica.view) {
                return standardRequest(REQUEST_METHOD.GET, APIS.URLS.PERSONA_FISICA + params, null, { disableToast: true })
            }
            else return []
        }

        const personasJuridicasRequest = async () => {
            if (access.juridica.view) {
                return standardRequest(REQUEST_METHOD.GET, APIS.URLS.PERSONA_JURIDICA + params, null, { disableToast: true })
            }
            else return []
        }

        return Promise.all([personasFisicasRequest(), personasJuridicasRequest()])
        .then(async (result) => {
            let sum = []

            result[0].forEach(({ id, idTipoDocumento, numeroDocumento, nombre, apellido }) => {
                sum.push({ id, idTipoDocumento, numeroDocumento, nombre: `${nombre} ${apellido}`, tipo: 'fisica' })
            })
            result[1].forEach(({ id, idTipoDocumento, numeroDocumento, nombreFantasia }) => {
                sum.push({ id, idTipoDocumento, numeroDocumento, nombre: nombreFantasia, tipo: 'juridica' })
            })
    
            setPersonas(sum)
            setTableVisible(true)
            return true
        }).catch(e => {
            if (e?.businessCode === BUSINESS_CODE.MAX_QUERY_SIZE_EXCEEDED) {
                setPersonas([])
                setTableVisible(false)
                setSearchMessage(VALIDATION_MESSAGE.TOO_MANY_RESULTS)
                return false
            }
            onRequestError(e)
            return false
        }).finally(() => {
            setIsLoading(false)
        })
    }

    function ApplyFilters() {
        setLabels(labels.map(label => ({ ...label, value: formValues[label.field] })))
        if (!hasActiveFilters) {
            setPersonas([])
            setTableVisible(false)
            setSearchMessage(VALIDATION_MESSAGE.REQUIRE_FILTER)
            return Promise.resolve(false)
        }
        return SearchPersonas()
    }
    
    //#endregion

    //#region Table

    const onView = (persona) => {
        navigate({ to: `/persona-${persona.tipo}/view/${persona.id}`})
    }

    const onModify = (persona) => {
        navigate({ to: `/persona-${persona.tipo}/edit/${persona.id}`})
    }

    const getDescTipoDocumento = (id) => {
        const row = getRowLista('TipoDocumento', id);
        return (row) ? row.nombre : '';
    }

    const tableColumns = [
        {
            Header: 'Tipo de persona',
            id: 'tipo',
            accessor: 'tipo',
            width: '15%',
            Cell: ({ row: { original }}) => original.tipo === 'fisica' ? `Física` : `Jurídica`,
        },{
            Header: 'Documento',
            Cell: ({ row: { original }}) => `${getDescTipoDocumento(original.idTipoDocumento)} ${original.numeroDocumento}`,
            id: 'documento',
            accessor: 'documento',
            width: '25%'
        },{
            Header: 'Nombre', id: 'nombre', accessor: 'nombre',
        },{
            id:'abm', accessor: 'id', width: '1%', disableGlobalFilter: true, disableSortBy: true,
            Header: '',
            Cell: ({ row }) =>  (
                <div className='action'>
                    {access[row.original.tipo]?.view && <div onClick={() => onView(row.original)} className="link">
                        <span className="material-symbols-outlined" title="Ver">search</span>
                    </div>}
                    {access[row.original.tipo]?.edit && <div onClick={() => onModify(row.original)} className="link">
                        <span className="material-symbols-outlined" title="Modificar">stylus</span>
                    </div>}
                </div>
            ),
        }
    ];

    //#endregion

    return <>

        <SectionHeading titles={['Búsqueda de personas']} />

        <section className='section-accordion'>

            <AdvancedSearch
                formValues={formValues}
                formSet={formSet}
                labels={labels.filter(f => f.value !== f.valueIgnore)}
                onSearch={ApplyFilters}
                message={searchMessage}
            >

                <div className='row form-basic'>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="numeroDocumento" className="form-label">Número de documento</label>
                        <InputWithEnterEvent
                            useInputFormat={true}
                            name="numeroDocumento"
                            placeholder=""
                            className="form-control"
                            mask={MASK_FORMAT.DOCUMENTO}
                            maskPlaceholder={null}
                            value={formValues.numeroDocumento}
                            onChange={formHandle}
                            onEnter={ApplyFilters}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-8">
                        <label htmlFor="nombre" className="form-label">Nombre</label>
                        <InputWithEnterEvent
                            name="nombre"
                            type="text"
                            placeholder=""
                            className="form-control"
                            value={formValues.nombre}
                            onChange={formHandle}
                            onEnter={ApplyFilters}
                        />
                    </div>
                </div>

            </AdvancedSearch>

            {tableVisible && (
                <TableCustom
                    className={'TableCustomBase m-top-20'}
                    columns={tableColumns}
                    data={personas}
                    messageEmpty="No se encontraron resultados"
                />
            )}

        </section>
    </>
}

export default HomeView;
